import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Link,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getDashboardStats } from '../api';
import { useAuth } from '../context/AuthContext';

const SECTION_COLORS = {
  all: { bg: '#e3f2fd', border: '#90caf9' },
  male: { bg: '#e8eaf6', border: '#9fa8da' },
  female: { bg: '#fce4ec', border: '#f48fb1' },
};

const SECTION_ORDER = ['all', 'male', 'female'];

function StatRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

function StatsSection({ data, sectionKey }) {
  if (!data) return null;
  const colors = SECTION_COLORS[sectionKey] || SECTION_COLORS.all;
  const label = data.label || sectionKey;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        height: '100%',
      }}
    >
      <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
        {data.count} {label}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <StatRow label="Adults" value={data.adults_count ?? 0} />
        <StatRow label="Teenagers" value={data.teenagers_count ?? 0} />
        <StatRow label="Children" value={data.children_count ?? 0} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5, pb: 0.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            {data.forms_completed ?? 0} Forms Completed
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            {data.forms_completed_pct ?? 0}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Involved
            <Tooltip title="are involved in 1 or more ministry areas">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data.involved}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Fresh
            <Tooltip title="Number of forms completed within the last year">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data.fresh ?? 0}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Aging
            <Tooltip title={`Forms older than 1 year but less than ${data.eform_expires_after_years ?? 3} years`}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data.aging ?? 0}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Stale
            <Tooltip title={`Number of forms that are older than ${data.eform_expires_after_years ?? 3} years`}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data.stale ?? 0}
          </Typography>
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ pt: 1.5, pb: 0.5 }}>
          {data.recently_sent_count ?? 0} Forms Recently Sent (last {data.recently_sent_days ?? 30} days)
        </Typography>
        {data.form_status && (
          <>
            <StatRow label="Status: Unopened" value={data.form_status.sent ?? 0} />
            <StatRow label="Status: Opened" value={data.form_status.opened ?? 0} />
            <StatRow label="Status: In progress" value={data.form_status['in-progress'] ?? 0} />
            <StatRow label="Status: Closed" value={data.form_status.closed ?? 0} />
          </>
        )}

        <StatRow label="Avg Time to Complete" value={data.time_to_complete} />
        

      </Box>
    </Paper>
  );
}

function GenderSummaryCard({ incompleteData, incompleteLabel, incompleteSeeAllUrl, staleData, staleLabel, staleSeeAllUrl, belowMinData, belowMinLabel, belowMinSeeAllUrl }) {
  const hasIncomplete = incompleteData && incompleteData.total > 0;
  const hasStale = staleData && staleData.total > 0;
  const hasBelowMin = belowMinData && belowMinData.total > 0;
  if (!hasIncomplete && !hasStale && !hasBelowMin) return null;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: '#fff8e1',
        border: '1px solid #ffe082',
        height: '100%',
      }}
    >
      {hasIncomplete && (
        <>
          <Typography variant="h6" gutterBottom fontWeight={700} color="error.dark">
            {incompleteData.total} Adult {incompleteLabel} need to complete the form
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Here are a few of them below. (<Link component={RouterLink} to={incompleteSeeAllUrl}>see all</Link>)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: (hasStale || hasBelowMin) ? 3 : 0 }}>
            {incompleteData.members.map((m) => (
              <Box
                key={m.id}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {m.first_name} {m.last_name}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {hasStale && (
        <>
          <Typography variant="h6" gutterBottom fontWeight={700} color="warning.dark">
            {staleData.total} Adult {staleLabel} have out-of-date Forms          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
           Interest forms older than {staleData.years} years (<Link component={RouterLink} to={staleSeeAllUrl}>see all</Link>)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
              Completed
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: hasBelowMin ? 3 : 0 }}>
            {staleData.members.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {m.first_name} {m.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right' }}>
                  {new Date(m.form_completed_at + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {hasBelowMin && (
        <>
          <Typography variant="h6" gutterBottom fontWeight={700} color="warning.dark">
            {belowMinData.total} Adult {belowMinLabel} Ministry Areas in Need of Help
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Ministry areas that need more involvement (<Link component={RouterLink} to={belowMinSeeAllUrl}>see all</Link>)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', px: 1.5, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: 'right' }}>
              Count
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: 'right' }}>
              Min
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {belowMinData.items.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {row.ministry}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexShrink: 0 }}>
                  <Typography variant="body2" fontWeight={600} color="error.main" sx={{ minWidth: 50, textAlign: 'right' }}>
                    {row.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, textAlign: 'right' }}>
                    {row.minimum}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}

function MemberListCard({ openForms = [], recentlySent = [], upcomingBirthdays = [], recentlySentDays = 30, upcomingBirthdaysDays = 90 }) {
  const formatBirthday = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: '#f5f5f5',
        border: '1px solid #e0e0e0',
      }}
    >
      <Grid container spacing={0} sx={{ gap: '10px' }}>
        <Grid item xs={12} md={6} sx={{ minWidth: { md: 420 } }}>
          <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
            Forms Recently Sent ({recentlySentDays} days)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Forms sent in the last {recentlySentDays} days
          </Typography>
          {recentlySent.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '24px', px: 1.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                Sent
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                Status
              </Typography>
            </Box>
          )}
          {recentlySent.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No forms sent in the last {recentlySentDays} days
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {recentlySent.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {m.first_name} {m.last_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                      {formatBirthday(m.form_sent_at)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                      {m.form_status}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6} sx={{ minWidth: { md: 'calc(50% + 180px)' } }}>
          <Typography variant="h6" gutterBottom fontWeight={700} color="primary">
            Upcoming Birthdays (next {upcomingBirthdaysDays} days)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Members with birthdays in the next {upcomingBirthdaysDays} days
          </Typography>
          {upcomingBirthdays.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', px: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 95 }}>
                Birthday
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                Completed
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 65 }}>
                Age
              </Typography>
            </Box>
          )}
          {upcomingBirthdays.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No upcoming birthdays
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {upcomingBirthdays.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {m.first_name} {m.last_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 95 }}>
                      {formatBirthday(m.birthday)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                      {m.completed_at ? formatDate(m.completed_at) : '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 65 }}>
                      {m.involvement_age ?? '—'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}

export default function Dashboard() {
  const { isAdmin, canViewGenderSummary } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getDashboardStats();
        if (mounted) setStats(data);
      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ maxWidth: 560 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {`Members are synced daily from the wordpress church admin database.  
        Adults, teenagers and children are imported.  
        A few members (${stats?.male?.excluded_count ?? 0} men, ${stats?.female?.excluded_count ?? 0} women) are 
        excluded from filling out the interest forms (various reasons) and are not included in the data below.  The administrator is the only user that can exclude members.`} 
      </Typography>
      <Grid container spacing={3}>
        {SECTION_ORDER.map((key) => (
          <Grid
            key={key}
            item
            xs={12}
            md={4}
            sx={{ flex: { md: '1 1 0' }, minWidth: { md: 300 } }}
          >
            <StatsSection data={stats?.[key]} sectionKey={key} />
          </Grid>
        ))}
        {canViewGenderSummary && (stats?.incomplete_form_men?.total > 0 || stats?.stale_form_men?.total > 0 || stats?.below_minimum_men?.total > 0) && (
          <Grid item xs={12} md={6}>
            <GenderSummaryCard
              incompleteData={stats.incomplete_form_men}
              incompleteLabel="Men"
              incompleteSeeAllUrl="/admin/members?male=1&female=0&form_filter=no_form&type=adult"
              staleData={stats.stale_form_men}
              staleLabel="Men"
              staleSeeAllUrl="/admin/members?male=1&female=0&form_filter=stale&type=adult"
              belowMinData={stats.below_minimum_men}
              belowMinLabel="Men's"
              belowMinSeeAllUrl="/admin/ministry-areas?low=1&male=1&female=0"
            />
          </Grid>
        )}
        {canViewGenderSummary && (stats?.incomplete_form_women?.total > 0 || stats?.stale_form_women?.total > 0 || stats?.below_minimum_women?.total > 0) && (
          <Grid item xs={12} md={6}>
            <GenderSummaryCard
              incompleteData={stats.incomplete_form_women}
              incompleteLabel="Women"
              incompleteSeeAllUrl="/admin/members?male=0&female=1&form_filter=no_form&type=adult"
              staleData={stats.stale_form_women}
              staleLabel="Women"
              staleSeeAllUrl="/admin/members?male=0&female=1&form_filter=stale&type=adult"
              belowMinData={stats.below_minimum_women}
              belowMinLabel="Women's"
              belowMinSeeAllUrl="/admin/ministry-areas?low=1&male=0&female=1"
            />
          </Grid>
        )}
        {isAdmin && (
          <Grid item xs={12}>
            <MemberListCard
              openForms={stats?.open_forms ?? []}
              recentlySent={stats?.recently_sent ?? []}
              upcomingBirthdays={stats?.upcoming_birthdays ?? []}
              recentlySentDays={stats?.recently_sent_days ?? 30}
              upcomingBirthdaysDays={stats?.upcoming_birthdays_days ?? 90}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
